import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { firstValueFrom, timeout } from "rxjs";

import { EnvMicroDocument } from "../config/env.micro.document";
import { DOCUMENT_ENV } from "../config/micros-env.tokens";
import { SessionCtl } from "./session.ctl.service";
import {
  ChatUploadMediaKind,
  ChatUploadMode,
} from "../../domain/models/messaging/chat-ui.model";
import {
  ChatMediaUrlPolicy,
  isOpaqueDocumentKey,
} from "./chat-media-url-policy.service";

export const CHAT_ATTACHMENT_FOLDER_NUMBER = "2";
/** Máximo tiempo para un PUT de una parte (hasta CHAT_UPLOAD_PART_BYTES) antes de considerarla colgada y forzar el retry con URL fresca. */
export const CHAT_UPLOAD_PART_TIMEOUT_MS = 3 * 60 * 1000;
/** Máximo tiempo para las llamadas de control (init/urls/parts/delete) del upload de chat: payload JSON chico, sin excusa para colgarse. */
export const CHAT_UPLOAD_CONTROL_TIMEOUT_MS = 30 * 1000;
/**
 * `complete` necesita su propio presupuesto, más amplio que el resto de las
 * llamadas de control. Aunque el payload sigue siendo JSON chico, del lado del
 * servidor esa llamada dispara un CompleteMultipartUpload y un CopyObject de
 * staging a la key final: para un archivo cercano al límite de 2GB esa copia
 * server-side tarda decenas de segundos aunque los bytes nunca pasen por el
 * backend. Con los 30s de CHAT_UPLOAD_CONTROL_TIMEOUT_MS el cliente abortaba
 * mientras el servidor seguía trabajando, y la burbuja quedaba clavada en 100%
 * sin error ni confirmación.
 */
export const CHAT_UPLOAD_COMPLETE_TIMEOUT_MS = 2 * 60 * 1000;
export const CHAT_MEDIA_WRITES_BLOCKED_ERROR_MESSAGE =
  "New chat media uploads are temporarily unavailable.";

export type ChatAttachmentPurpose =
  | "MESSAGE_ATTACHMENT"
  | "GROUP_AVATAR_CREATE"
  | "GROUP_AVATAR_UPDATE";

export interface ChatAttachmentUpload {
  reference: string;
  receipt: string;
  mimeType: string;
  fileName: string;
  fileSize: number;
}

export interface ChatUploadInitFile {
  clientFileId: string;
  name: string;
  declaredMime: string;
  size: number;
  mediaKind: ChatUploadMediaKind;
  durationMs?: number | null;
}

export interface ChatUploadInitRequest {
  batchId: string;
  files: ChatUploadInitFile[];
}

export interface ChatUploadPresignedUrl {
  partNumber?: number | null;
  url: string;
  expiresAt: string;
}

export interface ChatUploadInitItem {
  clientFileId: string;
  sessionId: string;
  capability: string;
  mode: ChatUploadMode;
  partSize?: number | null;
  totalParts?: number | null;
  urls: ChatUploadPresignedUrl[];
}

export interface ChatUploadInitResponse {
  items: ChatUploadInitItem[];
  sessionExpiresAt: string;
}

export interface ChatUploadUrlsResponse {
  sessionId: string;
  mode: ChatUploadMode;
  urls: ChatUploadPresignedUrl[];
}

export interface ChatUploadPart {
  partNumber: number;
  etag: string;
  size: number;
}

export interface ChatUploadPartsResponse {
  mode: ChatUploadMode;
  uploaded: boolean;
  etag?: string | null;
  parts?: ChatUploadPart[] | null;
}

export interface ChatUploadCompleteManifest {
  mode: ChatUploadMode;
  etag?: string | null;
  parts?: ChatUploadPart[] | null;
}

export interface ChatUploadCompleteResponse {
  reference: string;
  receipt: string;
  actualMime: string;
  size: number;
  mediaKind: ChatUploadMediaKind;
  durationMs?: number | null;
  receiptExpiresAt: string;
}

export class ChatUploadApiError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly retryable: boolean,
    readonly requestId: string | null,
    readonly status: number,
  ) {
    super(message);
    this.name = "ChatUploadApiError";
  }
}

/**
 * Minimal XHR PUT seam used by the presigned S3 upload flow. Lives behind the
 * service so the manager and tests never touch XMLHttpRequest directly.
 */
@Injectable({ providedIn: "root" })
export class ChatUploadXhrTransport {
  put(
    url: string,
    blob: Blob,
    onProgress?: (loadedBytes: number) => void,
    /**
     * Corta el PUT en curso. Sin esto, cancelar una subida solo dejaba de
     * ENCOLAR partes nuevas: las que ya estaban en vuelo seguían enviando sus
     * bytes a S3 hasta terminar. Con 4 partes concurrentes de 16MB eso son
     * hasta 64MB que se siguen subiendo después de apretar "Cancelar" — y en
     * modo SINGLE, el archivo entero.
     */
    signal?: AbortSignal,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(abortError());
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      if (blob.type) xhr.setRequestHeader("Content-Type", blob.type);
      // Sin timeout, una parte que se cuelga a mitad de subida (frecuente en
      // archivos grandes con muchas partes, ej. 1.5GB) nunca dispara onload
      // ni onerror: la promesa queda pendiente para siempre y el bubble se ve
      // "atascado" sin error visible. El timeout la fuerza a rechazar, lo que
      // dispara el retry-con-URL-fresca existente en uploadPartWithRetry.
      xhr.timeout = CHAT_UPLOAD_PART_TIMEOUT_MS;

      const onAbort = () => xhr.abort();
      signal?.addEventListener("abort", onAbort, { once: true });
      const release = () => signal?.removeEventListener("abort", onAbort);

      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable && onProgress) onProgress(ev.loaded);
      };
      xhr.onload = () => {
        release();
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader("ETag");
          resolve(etag ? etag.replace(/"/g, "") : "");
        } else {
          reject(
            new Error(`S3 PUT failed: HTTP ${xhr.status} (${xhr.statusText})`),
          );
        }
      };
      xhr.onerror = () => { release(); reject(new Error("S3 PUT network error")); };
      xhr.ontimeout = () => { release(); reject(new Error("S3 PUT timed out")); };
      xhr.onabort = () => { release(); reject(abortError()); };
      xhr.send(blob);
    });
  }
}

@Injectable({ providedIn: "root" })
export class DocumentS3UploadService {
  constructor(
    private readonly http: HttpClient,
    private readonly sessionCtl: SessionCtl,
    @Inject(DOCUMENT_ENV) private readonly documentEnv: EnvMicroDocument,
    private readonly mediaUrlPolicy: ChatMediaUrlPolicy,
    private readonly transport: ChatUploadXhrTransport,
  ) {}

  uploadGroupAvatar(
    file: File,
    purpose?: "GROUP_AVATAR_CREATE" | "GROUP_AVATAR_UPDATE",
  ): Promise<string | ChatAttachmentUpload> {
    return this.uploadForMode(file, purpose);
  }

  private async uploadForMode(
    file: File,
    purpose?: ChatAttachmentPurpose,
  ): Promise<string | ChatAttachmentUpload> {
    if (this.documentEnv.mediaWriteMode === "blocked") {
      throw new Error(CHAT_MEDIA_WRITES_BLOCKED_ERROR_MESSAGE);
    }
    if (this.documentEnv.mediaWriteMode === "receipt-required") {
      if (!purpose) {
        throw new Error("Chat media purpose is required.");
      }
      if (!this.documentEnv.mediaAllowedOrigins?.length) {
        throw new Error("Chat media reference origin is not configured.");
      }
      return this.uploadWithReceipt(file, purpose);
    }
    return this.upload(file, CHAT_ATTACHMENT_FOLDER_NUMBER);
  }

  private async uploadWithReceipt(
    file: File,
    purpose: ChatAttachmentPurpose,
  ): Promise<ChatAttachmentUpload> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("purpose", purpose);
    const response = await firstValueFrom(
      this.http.post<unknown>(`${this.uploadUrl}/chat/attachments`, formData, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.sessionCtl.getAccessTokenOrThrow()}`,
        }),
      }),
    );
    return parseChatAttachmentResponse(response, this.mediaUrlPolicy);
  }

  async upload(
    file: File,
    folderNumber = CHAT_ATTACHMENT_FOLDER_NUMBER,
  ): Promise<string> {
    if (this.documentEnv.mediaWriteMode !== "legacy-unverified") {
      throw new Error(CHAT_MEDIA_WRITES_BLOCKED_ERROR_MESSAGE);
    }
    return this.uploadToFolder(file, folderNumber);
  }

  /** Subida de documentos de requerimientos al endpoint general de ms-document. */
  uploadRequirementAttachment(file: File, folderNumber: string): Promise<string> {
    if (this.documentEnv.mediaWriteMode === "blocked") {
      throw new Error(CHAT_MEDIA_WRITES_BLOCKED_ERROR_MESSAGE);
    }
    return this.uploadToFolder(file, folderNumber);
  }

  private async uploadToFolder(file: File, folderNumber: string): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folderNumber", String(folderNumber));

    const response = await firstValueFrom(
      this.http.post<unknown>(this.uploadUrl, formData, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.sessionCtl.getAccessTokenOrThrow()}`,
        }),
      }),
    );
    return parseDocumentS3UploadResponse(
      response,
      this.mediaUrlPolicy,
      this.documentEnv.allowLegacyMediaKeys === true,
    );
  }

  private get uploadUrl(): string {
    return `${trimTrailingSlash(this.documentEnv.host)}${ensureLeadingSlash(this.documentEnv.apiBasePath)}`;
  }

  private get chatUploadUrl(): string {
    return `${this.uploadUrl}/chat/uploads`;
  }

  async initChatUpload(
    request: ChatUploadInitRequest,
  ): Promise<ChatUploadInitResponse> {
    const response = await this.post(this.chatUploadUrl, request, null);
    return parseChatUploadInitResponse(response);
  }

  async refreshChatUploadUrls(
    sessionId: string,
    capability: string,
    partNumbers?: number[],
  ): Promise<ChatUploadUrlsResponse> {
    const response = await this.post(
      `${this.chatUploadUrl}/${sessionId}/urls`,
      partNumbers?.length ? { partNumbers } : {},
      capability,
    );
    return parseChatUploadUrlsResponse(response);
  }

  async listChatUploadParts(
    sessionId: string,
    capability: string,
  ): Promise<ChatUploadPartsResponse> {
    const response = await this.get(
      `${this.chatUploadUrl}/${sessionId}/parts`,
      capability,
    );
    return parseChatUploadPartsResponse(response);
  }

  async completeChatUpload(
    sessionId: string,
    capability: string,
    manifest: ChatUploadCompleteManifest,
  ): Promise<ChatUploadCompleteResponse> {
    const response = await this.post(
      `${this.chatUploadUrl}/${sessionId}/complete`,
      manifest,
      capability,
      CHAT_UPLOAD_COMPLETE_TIMEOUT_MS,
    );
    return parseChatUploadCompleteResponse(response);
  }

  async deleteChatUpload(
    sessionId: string,
    capability: string,
  ): Promise<void> {
    await this.delete(
      `${this.chatUploadUrl}/${sessionId}`,
      capability,
    );
  }

  uploadSingle(
    url: string,
    file: File,
    onProgress?: (loadedBytes: number) => void,
    signal?: AbortSignal,
  ): Promise<string> {
    return this.transport.put(url, file.slice(0, file.size, file.type), onProgress, signal);
  }

  uploadPart(
    url: string,
    file: File,
    start: number,
    end: number,
    onProgress?: (loadedBytes: number) => void,
    signal?: AbortSignal,
  ): Promise<string> {
    return this.transport.put(url, file.slice(start, end, ""), onProgress, signal);
  }

  private async post<T>(
    url: string,
    body: unknown,
    capability: string | null,
    timeoutMs: number = CHAT_UPLOAD_CONTROL_TIMEOUT_MS,
  ): Promise<T> {
    return firstValueFrom(
      this.http.post<T>(url, body, {
        headers: this.chatHeaders(capability),
      }).pipe(timeout(timeoutMs)),
    );
  }

  private async get<T>(url: string, capability: string): Promise<T> {
    return firstValueFrom(
      this.http.get<T>(url, {
        headers: this.chatHeaders(capability),
      }).pipe(timeout(CHAT_UPLOAD_CONTROL_TIMEOUT_MS)),
    );
  }

  private async delete(url: string, capability: string): Promise<void> {
    await firstValueFrom(
      this.http.delete<void>(url, {
        headers: this.chatHeaders(capability),
      }).pipe(timeout(CHAT_UPLOAD_CONTROL_TIMEOUT_MS)),
    );
  }

  private chatHeaders(capability: string | null): HttpHeaders {
    let headers = new HttpHeaders({
      Authorization: `Bearer ${this.sessionCtl.getAccessTokenOrThrow()}`,
    });
    if (capability) {
      headers = headers.set("X-Chat-Upload-Capability", capability);
    }
    return headers;
  }
}

function parseChatUploadInitResponse(
  response: unknown,
): ChatUploadInitResponse {
  const record = readRecord(response);
  const rawItems = record?.["items"];
  const sessionExpiresAt = readString(record, "sessionExpiresAt");
  if (!Array.isArray(rawItems) || !sessionExpiresAt) {
    throw new Error("Document API returned an invalid chat upload INIT response.");
  }
  const items: ChatUploadInitItem[] = rawItems.map((item) => {
    const entry = readRecord(item);
    const clientFileId = readString(entry, "clientFileId");
    const sessionId = readString(entry, "sessionId");
    const capability = readString(entry, "capability");
    const mode = readString(entry, "mode");
    const rawUrls = entry?.["urls"];
    if (
      !clientFileId ||
      !sessionId ||
      !capability ||
      (mode !== "SINGLE" && mode !== "MULTIPART") ||
      !Array.isArray(rawUrls)
    ) {
      throw new Error("Document API returned an invalid chat upload INIT item.");
    }
    return {
      clientFileId,
      sessionId,
      capability,
      mode,
      partSize: numberOrNull(entry?.["partSize"]),
      totalParts: numberOrNull(entry?.["totalParts"]),
      urls: rawUrls.map(parseChatUploadUrl),
    };
  });
  return { items, sessionExpiresAt };
}

function parseChatUploadUrl(source: unknown): ChatUploadPresignedUrl {
  const record = readRecord(source);
  const url = readString(record, "url");
  const expiresAt = readString(record, "expiresAt");
  if (!url || !expiresAt) {
    throw new Error("Document API returned an invalid presigned upload URL.");
  }
  return {
    partNumber: numberOrNull(record?.["partNumber"]),
    url,
    expiresAt,
  };
}

function parseChatUploadUrlsResponse(
  response: unknown,
): ChatUploadUrlsResponse {
  const record = readRecord(response);
  const sessionId = readString(record, "sessionId");
  const mode = readString(record, "mode");
  const rawUrls = record?.["urls"];
  if (
    !sessionId ||
    (mode !== "SINGLE" && mode !== "MULTIPART") ||
    !Array.isArray(rawUrls)
  ) {
    throw new Error("Document API returned an invalid upload URL refresh response.");
  }
  return { sessionId, mode, urls: rawUrls.map(parseChatUploadUrl) };
}

function parseChatUploadPartsResponse(
  response: unknown,
): ChatUploadPartsResponse {
  const record = readRecord(response);
  const mode = readString(record, "mode");
  const rawParts = record?.["parts"];
  if (mode !== "SINGLE" && mode !== "MULTIPART") {
    throw new Error("Document API returned an invalid upload parts response.");
  }
  return {
    mode,
    uploaded: record?.["uploaded"] === true,
    etag: readString(record, "etag") ?? null,
    parts: Array.isArray(rawParts)
      ? rawParts.map((part) => {
          const entry = readRecord(part);
          const partNumber = entry?.["partNumber"];
          const etag = readString(entry, "etag");
          const size = entry?.["size"];
          if (
            typeof partNumber !== "number" ||
            !etag ||
            typeof size !== "number"
          ) {
            throw new Error("Document API returned an invalid upload part.");
          }
          return { partNumber, etag, size };
        })
      : null,
  };
}

function parseChatUploadCompleteResponse(
  response: unknown,
): ChatUploadCompleteResponse {
  const record = readRecord(response);
  const reference = readString(record, "reference");
  const receipt = readString(record, "receipt");
  const actualMime = readString(record, "actualMime");
  const size = record?.["size"];
  const mediaKind = readString(record, "mediaKind");
  const receiptExpiresAt = readString(record, "receiptExpiresAt");
  if (
    !reference ||
    !receipt ||
    !actualMime ||
    typeof size !== "number" ||
    (mediaKind !== "GENERAL" &&
      mediaKind !== "ATTACHED_AUDIO" &&
      mediaKind !== "VOICE_NOTE") ||
    !receiptExpiresAt
  ) {
    throw new Error("Document API returned an invalid chat upload COMPLETE response.");
  }
  return {
    reference,
    receipt,
    actualMime,
    size,
    mediaKind,
    durationMs: numberOrNull(record?.["durationMs"]),
    receiptExpiresAt,
  };
}

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

export function parseChatAttachmentResponse(
  response: unknown,
  policy: ChatMediaUrlPolicy,
): ChatAttachmentUpload {
  const envelope = readRecord(response);
  const data = readRecord(envelope?.["data"]);
  const reference = readString(data, "reference");
  const receipt = readString(data, "receipt");
  const mimeType = readString(data, "mimeType");
  const fileName = readString(data, "fileName");
  const fileSize = data?.["fileSize"];
  const canonicalReference = policy.resolveAbsolute(reference);
  if (
    envelope?.["result"] !== true ||
    envelope?.["status"] !== 200 ||
    !canonicalReference ||
    !receipt ||
    receipt.length > 6144 ||
    !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(receipt) ||
    !mimeType ||
    mimeType !== mimeType.toLowerCase() ||
    mimeType.length > 127 ||
    !fileName ||
    new TextEncoder().encode(fileName).length > 255 ||
    !Number.isSafeInteger(fileSize) ||
    (fileSize as number) < 1
  ) {
    throw new Error("Document API returned an invalid chat attachment receipt.");
  }
  return {
    reference: canonicalReference,
    receipt,
    mimeType,
    fileName,
    fileSize: fileSize as number,
  };
}

export function parseDocumentS3UploadResponse(
  response: unknown,
  policy: ChatMediaUrlPolicy,
  allowLegacyMediaKeys = false,
): string {
  const data = readRecord(response)?.["data"];
  const urlCandidates = [
    readString(response, "url"),
    readString(response, "fileUrl"),
    readString(data, "url"),
    readString(data, "fileUrl"),
    readPlainString(data),
  ];
  const finalUrl = urlCandidates
    .map((candidate) => policy.resolveAbsolute(candidate))
    .find((candidate): candidate is string => !!candidate);
  if (finalUrl) return finalUrl;

  if (!allowLegacyMediaKeys) {
    throw new Error(
      "Document API upload response did not include a supported final URL.",
    );
  }

  const keyCandidates = [
    readString(response, "s3Key"),
    readString(response, "key"),
    readString(data, "s3Key"),
    readString(data, "key"),
    readPlainString(data),
  ];
  const value = keyCandidates.find(isOpaqueDocumentKey);
  if (!value) {
    throw new Error(
      "Document API upload response did not include a supported S3 key or URL field.",
    );
  }

  return value;
}

function readString(source: unknown, key: string): string | undefined {
  const record = readRecord(source);
  const value = record?.[key];
  return typeof value === "string" ? value : undefined;
}

function readPlainString(source: unknown): string | undefined {
  return typeof source === "string" ? source : undefined;
}

function readRecord(source: unknown): Record<string, unknown> | undefined {
  return source && typeof source === "object"
    ? (source as Record<string, unknown>)
    : undefined;
}

/**
 * `AbortError` es el nombre que el resto de la plataforma usa para "lo cortó
 * el usuario", no "falló". El manager se apoya en su propio flag `cancelled`
 * para decidir, pero el nombre mantiene el rechazo distinguible de un error de
 * red en logs y en cualquier consumidor futuro.
 */
function abortError(): Error {
  if (typeof DOMException === "function") {
    return new DOMException("S3 PUT aborted", "AbortError");
  }
  const error = new Error("S3 PUT aborted");
  error.name = "AbortError";
  return error;
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function ensureLeadingSlash(value: string): string {
  return value.startsWith("/") ? value : `/${value}`;
}
